/**
 * Portfolio Calculations Utility
 *
 * Contains all calculation logic for the Loan Portfolio Analysis step.
 * Includes debt analysis, affordability scoring, and recommendation generation.
 */

/**
 * Calculate comprehensive debt analysis
 * @param {Array} existingLoans - Array of existing loan objects
 * @param {Object} selectedOffer - Selected offer object
 * @param {Object} eligibilityResult - Eligibility calculation result
 * @returns {Object} Analysis metrics
 */
export const calculateAnalysis = (existingLoans = [], selectedOffer = {}, eligibilityResult = {}, eligibilityInputs = {}, formData = {}) => {
    // Extract net salary from eligibility or offer
    // Priority: loan_eligibility.total_income > eligibilityInputs > formData > main applicant breakdown > selectedOffer
    const loanEligibility = eligibilityResult?.loan_eligibility || {};
    const mainApplicantBreakdown = loanEligibility?.applicants_breakdown?.find(a => a.type === 'main') ||
                                   loanEligibility?.applicants_breakdown?.[0] || {};

    const netSalary = parseFloat(
        // From loan eligibility total income (combined all applicants)
        loanEligibility?.total_income ||
        // From eligibility inputs (what user entered during eligibility check)
        eligibilityInputs?.net_salary ||
        eligibilityInputs?.salary ||
        // From form data (user-entered fields in the wizard)
        formData?.net_salary ||
        formData?.net_salary_per_month ||
        formData?.monthly_income ||
        formData?.salary ||
        // From main applicant breakdown
        mainApplicantBreakdown?.income ||
        // From selected offer
        selectedOffer?.net_salary_per_month ||
        selectedOffer?.net_salary ||
        // Legacy paths
        eligibilityResult?.applicant_data?.main_applicant?.net_salary ||
        eligibilityResult?.calculation_steps?.net_salary ||
        eligibilityResult?.eligibility_inputs?.net_salary ||
        0
    );

    // Calculate existing loans metrics
    const currentTotalEMI = existingLoans.reduce((sum, loan) =>
        sum + (parseFloat(loan.emi || loan.emi_amount) || 0), 0);

    const currentOutstanding = existingLoans.reduce((sum, loan) =>
        sum + (parseFloat(loan.outstanding_loan || loan.outstanding_amount) || 0), 0);

    const existingLoansCount = existingLoans.length;

    // Calculate average interest rate of existing loans
    const avgExistingRate = existingLoansCount > 0
        ? existingLoans.reduce((sum, loan) => sum + (parseFloat(loan.interest_rate) || 0), 0) / existingLoansCount
        : 0;

    // New loan metrics from selected offer
    const newEMI = parseFloat(selectedOffer?.emi) || 0;
    const newLoanAmount = parseFloat(selectedOffer?.loan_amount || selectedOffer?.principal) || 0;
    const newLoanRate = parseFloat(selectedOffer?.roi || selectedOffer?.interest_rate) || 0;
    const newLoanTenure = parseInt(selectedOffer?.tenure || selectedOffer?.tenure_months) || 0;

    // Combined metrics
    const combinedEMI = currentTotalEMI + newEMI;
    const combinedOutstanding = currentOutstanding + newLoanAmount;

    // FOIR calculations
    const foirBefore = netSalary > 0 ? (currentTotalEMI / netSalary) * 100 : 0;
    const foirAfter = netSalary > 0 ? (combinedEMI / netSalary) * 100 : 0;

    // EMI increase percentage
    const emiIncreasePercent = currentTotalEMI > 0
        ? (newEMI / currentTotalEMI) * 100
        : (newEMI > 0 ? 100 : 0);

    // Disposable income
    const disposableBefore = netSalary - currentTotalEMI;
    const disposableAfter = netSalary - combinedEMI;

    // Disposable income as percentage of salary
    const disposableBeforePercent = netSalary > 0 ? (disposableBefore / netSalary) * 100 : 0;
    const disposableAfterPercent = netSalary > 0 ? (disposableAfter / netSalary) * 100 : 0;

    // Annual income for debt-to-income ratio
    const annualIncome = netSalary * 12;
    const debtToIncomeRatio = annualIncome > 0 ? combinedOutstanding / annualIncome : 0;

    // Total interest calculations - use pre-calculated values when available
    const existingTotalInterest = existingLoans.reduce((sum, loan) => {
        // Use pre-calculated total_interest if available
        const totalInterest = parseFloat(loan.total_interest);
        if (totalInterest > 0) {
            return sum + totalInterest;
        }
        // Fallback calculation
        const tenure = parseInt(loan.remaining_tenure || loan.tenure_remaining) || 0;
        const emi = parseFloat(loan.emi || loan.emi_amount) || 0;
        const outstanding = parseFloat(loan.outstanding_loan || loan.outstanding_amount) || 0;
        return sum + Math.max(0, (emi * tenure) - outstanding);
    }, 0);

    // Calculate total interest paid till date across all loans
    const existingInterestPaid = existingLoans.reduce((sum, loan) => {
        return sum + (parseFloat(loan.interest_paid_till_date) || 0);
    }, 0);

    // Calculate remaining interest across all loans
    const existingInterestRemaining = existingLoans.reduce((sum, loan) => {
        return sum + (parseFloat(loan.outstanding_interest) || 0);
    }, 0);

    const newLoanTotalInterest = parseFloat(selectedOffer?.total_interest) ||
        (newEMI * newLoanTenure) - newLoanAmount;

    const lifetimeInterestCost = existingTotalInterest + Math.max(0, newLoanTotalInterest);

    // Debt Service Coverage Ratio (DSCR)
    const dscr = combinedEMI > 0 ? netSalary / combinedEMI : 0;

    // Emergency fund coverage (6 months of disposable income)
    const emergencyFundCoverage = disposableAfter * 6;

    // Monthly savings rate
    const monthlySavingsRate = netSalary > 0 ? (disposableAfter / netSalary) * 100 : 0;

    // Debt-free timeline (weighted average of remaining tenures)
    const maxExistingTenure = Math.max(...existingLoans.map(l => parseInt(l.remaining_tenure || l.tenure_remaining) || 0), 0);
    const debtFreeMonths = Math.max(maxExistingTenure, newLoanTenure);
    const debtFreeYears = Math.ceil(debtFreeMonths / 12);

    return {
        // Net salary
        netSalary,
        annualIncome,

        // Existing loans summary
        existingLoansCount,
        currentTotalEMI,
        currentOutstanding,
        avgExistingRate: parseFloat(avgExistingRate.toFixed(2)),
        existingTotalInterest,
        existingInterestPaid,
        existingInterestRemaining,

        // New loan details
        newEMI,
        newLoanAmount,
        newLoanRate,
        newLoanTenure,
        newLoanTotalInterest: Math.max(0, newLoanTotalInterest),

        // Combined metrics
        combinedEMI,
        combinedOutstanding,

        // FOIR
        foirBefore: parseFloat(foirBefore.toFixed(2)),
        foirAfter: parseFloat(foirAfter.toFixed(2)),
        foirChange: parseFloat((foirAfter - foirBefore).toFixed(2)),

        // EMI changes
        emiIncreasePercent: parseFloat(emiIncreasePercent.toFixed(2)),
        emiIncreaseAmount: newEMI,

        // Disposable income
        disposableBefore,
        disposableAfter,
        disposableBeforePercent: parseFloat(disposableBeforePercent.toFixed(2)),
        disposableAfterPercent: parseFloat(disposableAfterPercent.toFixed(2)),
        disposableChange: disposableAfter - disposableBefore,

        // Ratios
        debtToIncomeRatio: parseFloat(debtToIncomeRatio.toFixed(2)),
        dscr: parseFloat(dscr.toFixed(2)),

        // Additional metrics
        lifetimeInterestCost,
        emergencyFundCoverage,
        monthlySavingsRate: parseFloat(monthlySavingsRate.toFixed(2)),
        debtFreeMonths,
        debtFreeYears,

        // Rate comparison
        isNewRateLower: newLoanRate < avgExistingRate,
        rateDifference: parseFloat((avgExistingRate - newLoanRate).toFixed(2)),
    };
};

/**
 * Calculate affordability score (0-100)
 * @param {Object} analysis - Analysis metrics from calculateAnalysis
 * @returns {Object} Score details with breakdown
 */
export const calculateAffordabilityScore = (analysis) => {
    const {
        foirAfter,
        emiIncreasePercent,
        debtToIncomeRatio,
        disposableAfterPercent,
        netSalary,
        disposableAfter
    } = analysis;

    // FOIR Score (25 max)
    let foirScore = 5;
    let foirStatus = 'danger';
    if (foirAfter < 40) { foirScore = 25; foirStatus = 'success'; }
    else if (foirAfter < 50) { foirScore = 20; foirStatus = 'success'; }
    else if (foirAfter < 60) { foirScore = 15; foirStatus = 'warning'; }
    else if (foirAfter < 70) { foirScore = 10; foirStatus = 'warning'; }

    // EMI Increase Score (25 max)
    let emiScore = 5;
    let emiStatus = 'danger';
    if (emiIncreasePercent < 30) { emiScore = 25; emiStatus = 'success'; }
    else if (emiIncreasePercent < 50) { emiScore = 20; emiStatus = 'success'; }
    else if (emiIncreasePercent < 75) { emiScore = 15; emiStatus = 'warning'; }
    else if (emiIncreasePercent < 100) { emiScore = 10; emiStatus = 'warning'; }

    // Debt-to-Income Score (25 max)
    let dtiScore = 5;
    let dtiStatus = 'danger';
    if (debtToIncomeRatio < 3) { dtiScore = 25; dtiStatus = 'success'; }
    else if (debtToIncomeRatio < 4) { dtiScore = 20; dtiStatus = 'success'; }
    else if (debtToIncomeRatio < 5) { dtiScore = 15; dtiStatus = 'warning'; }
    else if (debtToIncomeRatio < 6) { dtiScore = 10; dtiStatus = 'warning'; }

    // Buffer Score (25 max) - based on disposable income after EMI
    const bufferPercent = netSalary > 0 ? (disposableAfter / netSalary) * 100 : 0;
    let bufferScore = 5;
    let bufferStatus = 'danger';
    if (bufferPercent > 50) { bufferScore = 25; bufferStatus = 'success'; }
    else if (bufferPercent > 40) { bufferScore = 20; bufferStatus = 'success'; }
    else if (bufferPercent > 30) { bufferScore = 15; bufferStatus = 'warning'; }
    else if (bufferPercent > 20) { bufferScore = 10; bufferStatus = 'warning'; }

    // Total score
    const totalScore = foirScore + emiScore + dtiScore + bufferScore;

    // Rating based on total score
    let rating = 'very_poor';
    let ratingLabel = 'Very Poor';
    let ratingColor = 'red';
    let ratingSeverity = 'danger';

    if (totalScore >= 85) {
        rating = 'excellent';
        ratingLabel = 'Excellent';
        ratingColor = '#22c55e';
        ratingSeverity = 'success';
    } else if (totalScore >= 70) {
        rating = 'good';
        ratingLabel = 'Good';
        ratingColor = '#84cc16';
        ratingSeverity = 'success';
    } else if (totalScore >= 50) {
        rating = 'moderate';
        ratingLabel = 'Moderate';
        ratingColor = '#eab308';
        ratingSeverity = 'warning';
    } else if (totalScore >= 25) {
        rating = 'poor';
        ratingLabel = 'Poor';
        ratingColor = '#f97316';
        ratingSeverity = 'warning';
    }

    // Risk level determination
    let riskLevel = 'medium';
    let riskLevelLabel = 'Medium';
    let riskColor = '#eab308';
    let riskSeverity = 'warning';

    if (totalScore >= 75 && foirAfter < 45) {
        riskLevel = 'low';
        riskLevelLabel = 'Low';
        riskColor = '#22c55e';
        riskSeverity = 'success';
    } else if (totalScore < 50 || foirAfter > 55) {
        riskLevel = 'high';
        riskLevelLabel = 'High';
        riskColor = '#ef4444';
        riskSeverity = 'danger';
    }

    return {
        totalScore,
        rating,
        ratingLabel,
        ratingColor,
        ratingSeverity,
        riskLevel,
        riskLevelLabel,
        riskColor,
        riskSeverity,
        breakdown: {
            foir: {
                score: foirScore,
                max: 25,
                status: foirStatus,
                label: 'FOIR After Loan',
                value: `${foirAfter.toFixed(1)}%`,
                description: foirAfter < 50 ? 'Within acceptable limit' : 'Exceeds recommended limit'
            },
            emi: {
                score: emiScore,
                max: 25,
                status: emiStatus,
                label: 'EMI Increase',
                value: `+${emiIncreasePercent.toFixed(0)}%`,
                description: emiIncreasePercent < 50 ? 'Manageable increase' : 'Significant increase'
            },
            dti: {
                score: dtiScore,
                max: 25,
                status: dtiStatus,
                label: 'Debt-to-Income',
                value: `${debtToIncomeRatio.toFixed(1)}x`,
                description: debtToIncomeRatio < 4 ? 'Healthy ratio' : 'High debt burden'
            },
            buffer: {
                score: bufferScore,
                max: 25,
                status: bufferStatus,
                label: 'Income Buffer',
                value: `${bufferPercent.toFixed(0)}%`,
                description: bufferPercent > 30 ? 'Good financial cushion' : 'Limited buffer'
            }
        }
    };
};

/**
 * Generate recommendations based on analysis
 * @param {Object} analysis - Analysis metrics
 * @param {Object} score - Affordability score details
 * @returns {Object} Recommendations and suggestions
 */
export const generateRecommendations = (analysis, score) => {
    const recommendations = []; // Warnings - action required
    const suggestions = []; // Info - good to know

    const {
        foirAfter,
        emiIncreasePercent,
        disposableAfterPercent,
        debtToIncomeRatio,
        dscr,
        isNewRateLower,
        rateDifference,
        newLoanRate,
        avgExistingRate,
        monthlySavingsRate,
        netSalary,
        disposableAfter
    } = analysis;

    // FOIR-based recommendations
    if (foirAfter > 55) {
        recommendations.push({
            type: 'warning',
            icon: 'pi-exclamation-triangle',
            severity: 'danger',
            title: 'High Debt Burden',
            message: `Your FOIR will exceed 55% (${foirAfter.toFixed(1)}%) after this loan. Consider a smaller loan amount or longer tenure to reduce EMI.`,
            action: 'Reduce loan amount or increase tenure'
        });
    } else if (foirAfter > 45) {
        suggestions.push({
            type: 'info',
            icon: 'pi-info-circle',
            severity: 'info',
            title: 'Moderate Debt Level',
            message: `Your FOIR will be ${foirAfter.toFixed(1)}% after this loan. Debt obligations will be manageable but leave limited room for additional credit.`,
            action: 'Avoid taking new loans for 12-18 months'
        });
    }

    // EMI Increase recommendations
    if (emiIncreasePercent > 100) {
        recommendations.push({
            type: 'warning',
            icon: 'pi-exclamation-triangle',
            severity: 'warning',
            title: 'Significant EMI Increase',
            message: `Your monthly EMI burden will more than double (+${emiIncreasePercent.toFixed(0)}%). Ensure you have a stable income source.`,
            action: 'Verify income stability before proceeding'
        });
    } else if (emiIncreasePercent > 50) {
        suggestions.push({
            type: 'info',
            icon: 'pi-info-circle',
            severity: 'info',
            title: 'Notable EMI Increase',
            message: `Your EMI will increase by ${emiIncreasePercent.toFixed(0)}%. Plan your monthly budget accordingly.`,
            action: 'Review monthly expenses'
        });
    }

    // Buffer recommendations
    const bufferPercent = disposableAfterPercent;
    if (bufferPercent < 20) {
        recommendations.push({
            type: 'caution',
            icon: 'pi-exclamation-circle',
            severity: 'warning',
            title: 'Limited Financial Buffer',
            message: `Only ${bufferPercent.toFixed(0)}% of your income will remain after all EMIs. Emergency expenses may be difficult to manage.`,
            action: 'Build emergency fund before taking loan'
        });
    } else if (bufferPercent < 30) {
        suggestions.push({
            type: 'info',
            icon: 'pi-info-circle',
            severity: 'info',
            title: 'Moderate Buffer',
            message: `You'll have ${bufferPercent.toFixed(0)}% of income as buffer. Consider maintaining an emergency fund.`,
            action: 'Maintain 3-6 months expenses as savings'
        });
    }

    // DSCR recommendation
    if (dscr < 1.5 && dscr > 0) {
        recommendations.push({
            type: 'warning',
            icon: 'pi-exclamation-triangle',
            severity: 'warning',
            title: 'Low Debt Service Coverage',
            message: `Your income covers EMI obligations only ${dscr.toFixed(1)}x times. A ratio above 1.5x is recommended for financial safety.`,
            action: 'Consider reducing loan amount'
        });
    }

    // Positive suggestions
    if (foirAfter < 40) {
        suggestions.push({
            type: 'success',
            icon: 'pi-check-circle',
            severity: 'success',
            title: 'Healthy Debt Ratio',
            message: 'Your debt-to-income ratio remains healthy. You have room for future financial goals.',
            action: null
        });
    }

    // Interest rate comparison
    if (isNewRateLower && avgExistingRate > 0) {
        suggestions.push({
            type: 'tip',
            icon: 'pi-lightbulb',
            severity: 'info',
            title: 'Consider Debt Consolidation',
            message: `The new loan rate (${newLoanRate}%) is lower than your existing average (${avgExistingRate.toFixed(1)}%). You could save by consolidating high-interest loans.`,
            action: 'Explore balance transfer options'
        });
    }

    // Savings rate suggestion
    if (monthlySavingsRate > 40) {
        suggestions.push({
            type: 'success',
            icon: 'pi-check-circle',
            severity: 'success',
            title: 'Excellent Savings Potential',
            message: `You'll still be able to save ${monthlySavingsRate.toFixed(0)}% of your income after all EMIs.`,
            action: null
        });
    }

    // Overall recommendation
    let overallRecommendation = {
        verdict: 'proceed',
        verdictLabel: 'Proceed with Application',
        verdictColor: '#22c55e',
        verdictSeverity: 'success',
        message: 'Based on the analysis, this loan appears affordable and manageable.'
    };

    if (score.riskLevel === 'high') {
        overallRecommendation = {
            verdict: 'caution',
            verdictLabel: 'Proceed with Caution',
            verdictColor: '#f97316',
            verdictSeverity: 'warning',
            message: 'This loan represents significant financial commitment. Consider the recommendations above before proceeding.'
        };
    } else if (score.riskLevel === 'medium') {
        overallRecommendation = {
            verdict: 'review',
            verdictLabel: 'Review Carefully',
            verdictColor: '#eab308',
            verdictSeverity: 'warning',
            message: 'The loan is manageable but will stretch your budget. Ensure stable income before committing.'
        };
    }

    return {
        recommendations,
        suggestions,
        overallRecommendation,
        hasWarnings: recommendations.length > 0,
        hasSuggestions: suggestions.length > 0
    };
};

/**
 * Format currency in Indian format
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    }).format(amount);
};

/**
 * Format percentage
 * @param {number} value - Value to format
 * @param {number} decimals - Decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return `${parseFloat(value).toFixed(decimals)}%`;
};

/**
 * Prepare portfolio data for API submission
 * @param {Object} analysis - Analysis metrics
 * @param {Object} score - Affordability score
 * @param {Object} recommendations - Recommendations object
 * @param {Array} existingLoans - Existing loans array
 * @param {Object} selectedOffer - Selected offer object
 * @param {Object} eligibilityResult - Eligibility result object
 * @returns {Object} Data ready for API submission
 */
export const preparePortfolioData = (
    analysis,
    score,
    recommendations,
    existingLoans,
    selectedOffer,
    eligibilityResult
) => {
    return {
        // Summary metrics
        total_existing_emi: analysis.currentTotalEMI,
        new_loan_emi: analysis.newEMI,
        combined_emi: analysis.combinedEMI,
        foir_before: analysis.foirBefore,
        foir_after: analysis.foirAfter,

        // Scores
        affordability_score: score.rating,
        risk_level: score.riskLevel,
        score_value: score.totalScore,

        // JSON data
        existing_loans_data: existingLoans,
        selected_offer_data: selectedOffer,
        eligibility_data: eligibilityResult,
        analysis_data: analysis,
        recommendations: recommendations
    };
};

/**
 * Calculate salary impact analysis
 * Shows how the new loan affects salary allocation
 * @param {Object} analysis - Analysis metrics from calculateAnalysis
 * @returns {Object} Salary impact breakdown
 */
export const calculateSalaryImpact = (analysis) => {
    const { netSalary, currentTotalEMI, newEMI, combinedEMI, disposableAfter } = analysis;

    // Define expense categories (estimates based on typical Indian household)
    const essentialExpenses = netSalary * 0.30; // 30% for essentials (food, utilities, transport)
    const discretionary = netSalary * 0.10; // 10% for discretionary spending
    const savings = Math.max(0, disposableAfter - essentialExpenses - discretionary);

    // Calculate allocations
    const allocations = {
        existingEMI: {
            amount: currentTotalEMI,
            percent: netSalary > 0 ? (currentTotalEMI / netSalary) * 100 : 0,
            label: 'Existing EMIs',
            color: '#3B82F6' // Blue
        },
        newEMI: {
            amount: newEMI,
            percent: netSalary > 0 ? (newEMI / netSalary) * 100 : 0,
            label: 'New Loan EMI',
            color: '#8B5CF6' // Purple
        },
        essentials: {
            amount: essentialExpenses,
            percent: netSalary > 0 ? (essentialExpenses / netSalary) * 100 : 0,
            label: 'Essential Expenses',
            color: '#F59E0B' // Amber
        },
        discretionary: {
            amount: discretionary,
            percent: netSalary > 0 ? (discretionary / netSalary) * 100 : 0,
            label: 'Discretionary',
            color: '#10B981' // Green
        },
        savings: {
            amount: savings,
            percent: netSalary > 0 ? (savings / netSalary) * 100 : 0,
            label: 'Savings',
            color: '#06B6D4' // Cyan
        }
    };

    // Calculate take-home impact
    const takeHomeBeforeLoan = netSalary - currentTotalEMI;
    const takeHomeAfterLoan = netSalary - combinedEMI;
    const takeHomeReduction = takeHomeBeforeLoan - takeHomeAfterLoan;
    const takeHomeReductionPercent = takeHomeBeforeLoan > 0 ? (takeHomeReduction / takeHomeBeforeLoan) * 100 : 0;

    // Determine health status
    let salaryHealth = 'healthy';
    let salaryHealthLabel = 'Healthy';
    let salaryHealthColor = '#22c55e';
    let salaryHealthSeverity = 'success';

    const totalEMIPercent = netSalary > 0 ? (combinedEMI / netSalary) * 100 : 0;
    if (totalEMIPercent > 60) {
        salaryHealth = 'critical';
        salaryHealthLabel = 'Critical';
        salaryHealthColor = '#ef4444';
        salaryHealthSeverity = 'danger';
    } else if (totalEMIPercent > 50) {
        salaryHealth = 'strained';
        salaryHealthLabel = 'Strained';
        salaryHealthColor = '#f97316';
        salaryHealthSeverity = 'warning';
    } else if (totalEMIPercent > 40) {
        salaryHealth = 'moderate';
        salaryHealthLabel = 'Moderate';
        salaryHealthColor = '#eab308';
        salaryHealthSeverity = 'warning';
    }

    return {
        allocations,
        takeHomeBeforeLoan,
        takeHomeAfterLoan,
        takeHomeReduction,
        takeHomeReductionPercent: parseFloat(takeHomeReductionPercent.toFixed(1)),
        totalEMIPercent: parseFloat(totalEMIPercent.toFixed(1)),
        salaryHealth,
        salaryHealthLabel,
        salaryHealthColor,
        salaryHealthSeverity,
        savingsAfterLoan: savings,
        savingsPercent: parseFloat(allocations.savings.percent.toFixed(1))
    };
};

/**
 * Calculate eligibility utilization
 * Shows how much borrowing capacity is being used
 * @param {Object} analysis - Analysis metrics
 * @param {Object} eligibilityResult - Eligibility calculation result
 * @param {Object} selectedOffer - Selected offer
 * @returns {Object} Eligibility utilization metrics
 */
export const calculateEligibilityUtilization = (analysis, eligibilityResult = {}, selectedOffer = {}) => {
    // Get maximum eligible amount
    const maxEligible = parseFloat(
        eligibilityResult?.eligible_amount ||
        eligibilityResult?.max_loan_amount ||
        selectedOffer?.max_loan_eligibility ||
        analysis.newLoanAmount * 1.5 // Estimate if not available
    );

    const requestedAmount = analysis.newLoanAmount;
    const utilizationPercent = maxEligible > 0 ? (requestedAmount / maxEligible) * 100 : 0;
    const remainingCapacity = Math.max(0, maxEligible - requestedAmount);
    const remainingPercent = 100 - utilizationPercent;

    // Determine utilization status
    let utilizationStatus = 'optimal';
    let utilizationLabel = 'Optimal';
    let utilizationColor = '#22c55e';
    let utilizationSeverity = 'success';

    if (utilizationPercent > 90) {
        utilizationStatus = 'maxed';
        utilizationLabel = 'Near Maximum';
        utilizationColor = '#ef4444';
        utilizationSeverity = 'danger';
    } else if (utilizationPercent > 75) {
        utilizationStatus = 'high';
        utilizationLabel = 'High Utilization';
        utilizationColor = '#f97316';
        utilizationSeverity = 'warning';
    } else if (utilizationPercent > 50) {
        utilizationStatus = 'moderate';
        utilizationLabel = 'Moderate';
        utilizationColor = '#eab308';
        utilizationSeverity = 'warning';
    }

    // FOIR-based capacity
    const foirLimit = 50; // Standard FOIR limit
    const foirUsed = analysis.foirAfter;
    const foirRemaining = Math.max(0, foirLimit - foirUsed);
    const additionalEMICapacity = (foirRemaining / 100) * analysis.netSalary;

    return {
        maxEligible,
        requestedAmount,
        utilizationPercent: parseFloat(utilizationPercent.toFixed(1)),
        remainingCapacity,
        remainingPercent: parseFloat(remainingPercent.toFixed(1)),
        utilizationStatus,
        utilizationLabel,
        utilizationColor,
        utilizationSeverity,
        foirLimit,
        foirUsed: parseFloat(foirUsed.toFixed(1)),
        foirRemaining: parseFloat(foirRemaining.toFixed(1)),
        additionalEMICapacity: parseFloat(additionalEMICapacity.toFixed(0))
    };
};

/**
 * Prepare loan comparison matrix data
 * @param {Array} existingLoans - Existing loans
 * @param {Object} selectedOffer - Selected offer
 * @param {Object} analysis - Analysis metrics
 * @returns {Array} Formatted loan comparison data
 */
export const prepareLoanComparisonMatrix = (existingLoans = [], selectedOffer = {}, analysis = {}) => {
    const loans = existingLoans.map((loan, index) => ({
        id: `existing-${index}`,
        type: 'existing',
        lender: loan.lender_name || loan.lender || 'Bank',
        loanType: loan.loan_type || loan.type || 'Loan',
        originalAmount: parseFloat(loan.original_loan_amount || loan.loan_amount || 0),
        outstanding: parseFloat(loan.outstanding_loan || loan.outstanding_amount || 0),
        emi: parseFloat(loan.emi || loan.emi_amount || 0),
        interestRate: parseFloat(loan.interest_rate || 0),
        tenureTotal: parseInt(loan.tenure_months || loan.tenure || 0),
        tenurePaid: parseInt(loan.paid_tenure || 0),
        tenureRemaining: parseInt(loan.remaining_tenure || loan.tenure_remaining || 0),
        progressPercent: loan.tenure_months > 0 ? Math.round((loan.paid_tenure / loan.tenure_months) * 100) : 0,
        // New calculated fields from ExistingLoanCalculationService
        principalPaid: parseFloat(loan.principal_paid_till_date || 0),
        interestPaid: parseFloat(loan.interest_paid_till_date || 0),
        emiPaid: parseFloat(loan.emi_paid_till_date || 0),
        remainingPrincipal: parseFloat(loan.remaining_principal || loan.outstanding_loan || 0),
        remainingInterest: parseFloat(loan.outstanding_interest || 0),
        remainingEmiTotal: parseFloat(loan.remaining_emi_total || 0),
        totalInterest: parseFloat(loan.total_interest || 0),
        totalEmiPayment: parseFloat(loan.total_emi_payment || 0)
    }));

    // Add new loan
    if (selectedOffer && analysis.newEMI > 0) {
        loans.push({
            id: 'new-loan',
            type: 'new',
            lender: selectedOffer.bank_name || selectedOffer.lender_name || 'New Lender',
            loanType: selectedOffer.product_name || 'New Loan',
            originalAmount: analysis.newLoanAmount,
            outstanding: analysis.newLoanAmount,
            emi: analysis.newEMI,
            interestRate: analysis.newLoanRate,
            tenureTotal: analysis.newLoanTenure,
            tenurePaid: 0,
            tenureRemaining: analysis.newLoanTenure,
            progressPercent: 0,
            // New loan has no paid amounts yet
            principalPaid: 0,
            interestPaid: 0,
            emiPaid: 0,
            remainingPrincipal: analysis.newLoanAmount,
            remainingInterest: analysis.newLoanTotalInterest,
            remainingEmiTotal: analysis.newEMI * analysis.newLoanTenure,
            totalInterest: analysis.newLoanTotalInterest,
            totalEmiPayment: analysis.newEMI * analysis.newLoanTenure
        });
    }

    // Calculate totals including new fields
    const totals = {
        totalOriginal: loans.reduce((sum, l) => sum + l.originalAmount, 0),
        totalOutstanding: loans.reduce((sum, l) => sum + l.outstanding, 0),
        totalEMI: loans.reduce((sum, l) => sum + l.emi, 0),
        avgRate: loans.length > 0 ? loans.reduce((sum, l) => sum + l.interestRate, 0) / loans.length : 0,
        loansCount: loans.length,
        // New aggregated fields
        totalPrincipalPaid: loans.reduce((sum, l) => sum + (l.principalPaid || 0), 0),
        totalInterestPaid: loans.reduce((sum, l) => sum + (l.interestPaid || 0), 0),
        totalEmiPaid: loans.reduce((sum, l) => sum + (l.emiPaid || 0), 0),
        totalRemainingPrincipal: loans.reduce((sum, l) => sum + (l.remainingPrincipal || 0), 0),
        totalRemainingInterest: loans.reduce((sum, l) => sum + (l.remainingInterest || 0), 0),
        totalRemainingEmi: loans.reduce((sum, l) => sum + (l.remainingEmiTotal || 0), 0),
        totalInterest: loans.reduce((sum, l) => sum + (l.totalInterest || 0), 0),
        totalEmiPayment: loans.reduce((sum, l) => sum + (l.totalEmiPayment || 0), 0)
    };

    return { loans, totals };
};

/**
 * Calculate monthly cash flow impact
 * @param {Object} analysis - Analysis metrics
 * @returns {Object} Cash flow comparison data
 */
export const calculateCashFlowImpact = (analysis) => {
    const { netSalary, currentTotalEMI, combinedEMI, newEMI, disposableBefore, disposableAfter } = analysis;

    // Before new loan
    const beforeLoan = {
        income: netSalary,
        emiOutflow: currentTotalEMI,
        available: disposableBefore,
        availablePercent: netSalary > 0 ? (disposableBefore / netSalary) * 100 : 0
    };

    // After new loan
    const afterLoan = {
        income: netSalary,
        emiOutflow: combinedEMI,
        available: disposableAfter,
        availablePercent: netSalary > 0 ? (disposableAfter / netSalary) * 100 : 0
    };

    // Changes
    const changes = {
        emiIncrease: newEMI,
        emiIncreasePercent: currentTotalEMI > 0 ? (newEMI / currentTotalEMI) * 100 : (newEMI > 0 ? 100 : 0),
        availableReduction: disposableBefore - disposableAfter,
        availableReductionPercent: disposableBefore > 0 ? ((disposableBefore - disposableAfter) / disposableBefore) * 100 : 0
    };

    // Determine impact severity
    let impactSeverity = 'low';
    let impactLabel = 'Low Impact';
    let impactColor = '#22c55e';

    if (changes.availableReductionPercent > 50) {
        impactSeverity = 'high';
        impactLabel = 'High Impact';
        impactColor = '#ef4444';
    } else if (changes.availableReductionPercent > 30) {
        impactSeverity = 'moderate';
        impactLabel = 'Moderate Impact';
        impactColor = '#f97316';
    } else if (changes.availableReductionPercent > 15) {
        impactSeverity = 'manageable';
        impactLabel = 'Manageable Impact';
        impactColor = '#eab308';
    }

    return {
        beforeLoan,
        afterLoan,
        changes: {
            ...changes,
            emiIncreasePercent: parseFloat(changes.emiIncreasePercent.toFixed(1)),
            availableReductionPercent: parseFloat(changes.availableReductionPercent.toFixed(1))
        },
        impactSeverity,
        impactLabel,
        impactColor
    };
};

/**
 * Calculate detailed risk assessment
 * @param {Object} analysis - Analysis metrics
 * @param {Object} score - Affordability score
 * @returns {Object} Risk assessment with factors
 */
export const calculateRiskAssessment = (analysis, score) => {
    const riskFactors = [];

    // FOIR Risk
    const foirRisk = {
        factor: 'FOIR Level',
        value: `${analysis.foirAfter.toFixed(1)}%`,
        status: analysis.foirAfter < 40 ? 'low' : analysis.foirAfter < 50 ? 'medium' : 'high',
        statusLabel: analysis.foirAfter < 40 ? 'Low Risk' : analysis.foirAfter < 50 ? 'Medium Risk' : 'High Risk',
        color: analysis.foirAfter < 40 ? '#22c55e' : analysis.foirAfter < 50 ? '#eab308' : '#ef4444',
        impact: analysis.foirAfter < 40 ? 10 : analysis.foirAfter < 50 ? 5 : -5,
        description: analysis.foirAfter < 40
            ? 'FOIR is within safe limits'
            : analysis.foirAfter < 50
            ? 'FOIR is approaching the limit'
            : 'FOIR exceeds recommended limit'
    };
    riskFactors.push(foirRisk);

    // Debt-to-Income Risk
    const dtiRisk = {
        factor: 'Debt-to-Income',
        value: `${analysis.debtToIncomeRatio.toFixed(1)}x`,
        status: analysis.debtToIncomeRatio < 3 ? 'low' : analysis.debtToIncomeRatio < 5 ? 'medium' : 'high',
        statusLabel: analysis.debtToIncomeRatio < 3 ? 'Low Risk' : analysis.debtToIncomeRatio < 5 ? 'Medium Risk' : 'High Risk',
        color: analysis.debtToIncomeRatio < 3 ? '#22c55e' : analysis.debtToIncomeRatio < 5 ? '#eab308' : '#ef4444',
        impact: analysis.debtToIncomeRatio < 3 ? 10 : analysis.debtToIncomeRatio < 5 ? 5 : -5,
        description: `Total debt is ${analysis.debtToIncomeRatio.toFixed(1)}x your annual income`
    };
    riskFactors.push(dtiRisk);

    // DSCR Risk
    const dscrRisk = {
        factor: 'Debt Service Coverage',
        value: `${analysis.dscr.toFixed(2)}x`,
        status: analysis.dscr > 2 ? 'low' : analysis.dscr > 1.5 ? 'medium' : 'high',
        statusLabel: analysis.dscr > 2 ? 'Low Risk' : analysis.dscr > 1.5 ? 'Medium Risk' : 'High Risk',
        color: analysis.dscr > 2 ? '#22c55e' : analysis.dscr > 1.5 ? '#eab308' : '#ef4444',
        impact: analysis.dscr > 2 ? 10 : analysis.dscr > 1.5 ? 5 : -5,
        description: `Income covers EMI ${analysis.dscr.toFixed(2)} times`
    };
    riskFactors.push(dscrRisk);

    // Income Buffer Risk
    const bufferPercent = analysis.disposableAfterPercent;
    const bufferRisk = {
        factor: 'Income Buffer',
        value: `${bufferPercent.toFixed(1)}%`,
        status: bufferPercent > 40 ? 'low' : bufferPercent > 25 ? 'medium' : 'high',
        statusLabel: bufferPercent > 40 ? 'Low Risk' : bufferPercent > 25 ? 'Medium Risk' : 'High Risk',
        color: bufferPercent > 40 ? '#22c55e' : bufferPercent > 25 ? '#eab308' : '#ef4444',
        impact: bufferPercent > 40 ? 10 : bufferPercent > 25 ? 5 : -5,
        description: `${bufferPercent.toFixed(0)}% of income remains after EMIs`
    };
    riskFactors.push(bufferRisk);

    // Loan Concentration Risk (multiple loans)
    const loanCountRisk = {
        factor: 'Loan Concentration',
        value: `${analysis.existingLoansCount + 1} loans`,
        status: analysis.existingLoansCount < 2 ? 'low' : analysis.existingLoansCount < 4 ? 'medium' : 'high',
        statusLabel: analysis.existingLoansCount < 2 ? 'Low Risk' : analysis.existingLoansCount < 4 ? 'Medium Risk' : 'High Risk',
        color: analysis.existingLoansCount < 2 ? '#22c55e' : analysis.existingLoansCount < 4 ? '#eab308' : '#ef4444',
        impact: analysis.existingLoansCount < 2 ? 10 : analysis.existingLoansCount < 4 ? 5 : -5,
        description: analysis.existingLoansCount < 2
            ? 'Few active loans - manageable'
            : 'Multiple active loans increase default risk'
    };
    riskFactors.push(loanCountRisk);

    // Calculate overall risk score
    const totalImpact = riskFactors.reduce((sum, f) => sum + f.impact, 0);
    const maxPossibleImpact = riskFactors.length * 10;
    const riskScore = Math.round(((totalImpact + maxPossibleImpact) / (maxPossibleImpact * 2)) * 100);

    // Determine overall risk level
    let overallRisk = 'low';
    let overallRiskLabel = 'Low Risk';
    let overallRiskColor = '#22c55e';

    const highRiskCount = riskFactors.filter(f => f.status === 'high').length;
    const mediumRiskCount = riskFactors.filter(f => f.status === 'medium').length;

    if (highRiskCount >= 2 || riskScore < 40) {
        overallRisk = 'high';
        overallRiskLabel = 'High Risk';
        overallRiskColor = '#ef4444';
    } else if (highRiskCount >= 1 || mediumRiskCount >= 2 || riskScore < 60) {
        overallRisk = 'medium';
        overallRiskLabel = 'Medium Risk';
        overallRiskColor = '#eab308';
    }

    return {
        riskFactors,
        riskScore,
        overallRisk,
        overallRiskLabel,
        overallRiskColor,
        highRiskCount,
        mediumRiskCount,
        lowRiskCount: riskFactors.filter(f => f.status === 'low').length
    };
};

/**
 * Calculate interest cost analysis
 * @param {Array} existingLoans - Existing loans
 * @param {Object} analysis - Analysis metrics
 * @returns {Object} Interest cost breakdown
 */
export const calculateInterestCostAnalysis = (existingLoans = [], analysis) => {
    // Calculate interest for each existing loan
    // Uses pre-calculated values from ExistingLoanCalculationService when available
    const existingLoansInterest = existingLoans.map((loan, index) => {
        const emi = parseFloat(loan.emi || loan.emi_amount || 0);
        const remaining = parseInt(loan.remaining_tenure || loan.tenure_remaining || 0);
        const outstanding = parseFloat(loan.outstanding_loan || loan.outstanding_amount || 0);
        const totalPayment = emi * remaining;

        // Use pre-calculated outstanding_interest if available, otherwise calculate
        const interestRemaining = parseFloat(loan.outstanding_interest) ||
            Math.max(0, totalPayment - outstanding);

        // Use pre-calculated total_interest if available
        const totalInterest = parseFloat(loan.total_interest) ||
            Math.max(0, (emi * parseInt(loan.tenure_months || 0)) - parseFloat(loan.original_loan_amount || 0));

        // Use pre-calculated interest_paid_till_date if available
        const interestPaid = parseFloat(loan.interest_paid_till_date) ||
            Math.max(0, totalInterest - interestRemaining);

        return {
            id: index,
            lender: loan.lender_name || loan.lender || 'Bank',
            loanType: loan.loan_type || loan.type || 'Loan',
            interestRate: parseFloat(loan.interest_rate || 0),
            interestRemaining,
            interestPaid,
            totalInterest,
            totalPayment,
            outstanding
        };
    });

    // New loan interest
    const newLoanInterest = {
        lender: 'New Loan',
        loanType: 'Proposed',
        interestRate: analysis.newLoanRate,
        interestRemaining: analysis.newLoanTotalInterest,
        totalPayment: analysis.newEMI * analysis.newLoanTenure,
        outstanding: analysis.newLoanAmount
    };

    // Totals
    const totalExistingInterest = existingLoansInterest.reduce((sum, l) => sum + l.interestRemaining, 0);
    const totalNewInterest = analysis.newLoanTotalInterest;
    const totalLifetimeInterest = totalExistingInterest + totalNewInterest;

    // Interest as percentage of principal
    const totalPrincipal = analysis.currentOutstanding + analysis.newLoanAmount;
    const interestToprrincipalRatio = totalPrincipal > 0 ? (totalLifetimeInterest / totalPrincipal) * 100 : 0;

    // Monthly interest cost
    const monthlyInterestCost = analysis.debtFreeMonths > 0 ? totalLifetimeInterest / analysis.debtFreeMonths : 0;

    // Determine cost status
    let costStatus = 'optimal';
    let costStatusLabel = 'Optimal';
    let costStatusColor = '#22c55e';

    if (interestToprrincipalRatio > 50) {
        costStatus = 'high';
        costStatusLabel = 'High Interest Burden';
        costStatusColor = '#ef4444';
    } else if (interestToprrincipalRatio > 30) {
        costStatus = 'moderate';
        costStatusLabel = 'Moderate';
        costStatusColor = '#eab308';
    }

    return {
        existingLoansInterest,
        newLoanInterest,
        totalExistingInterest,
        totalNewInterest,
        totalLifetimeInterest,
        totalPrincipal,
        interestToPrincipalRatio: parseFloat(interestToprrincipalRatio.toFixed(1)),
        monthlyInterestCost: parseFloat(monthlyInterestCost.toFixed(0)),
        costStatus,
        costStatusLabel,
        costStatusColor
    };
};

/**
 * Calculate debt freedom timeline
 * @param {Array} existingLoans - Existing loans
 * @param {Object} analysis - Analysis metrics
 * @returns {Object} Timeline data
 */
export const calculateDebtFreedomTimeline = (existingLoans = [], analysis) => {
    const today = new Date();
    const milestones = [];

    // Add existing loan end dates
    existingLoans.forEach((loan, index) => {
        const remaining = parseInt(loan.remaining_tenure || loan.tenure_remaining || 0);
        if (remaining > 0) {
            const endDate = new Date(today);
            endDate.setMonth(endDate.getMonth() + remaining);

            milestones.push({
                id: `existing-${index}`,
                type: 'loan-end',
                label: `${loan.lender_name || loan.lender || 'Loan'} closes`,
                date: endDate,
                monthsFromNow: remaining,
                emiFreed: parseFloat(loan.emi || loan.emi_amount || 0),
                loanType: loan.loan_type || loan.type || 'Loan'
            });
        }
    });

    // Add new loan end date
    if (analysis.newLoanTenure > 0) {
        const newLoanEndDate = new Date(today);
        newLoanEndDate.setMonth(newLoanEndDate.getMonth() + analysis.newLoanTenure);

        milestones.push({
            id: 'new-loan',
            type: 'loan-end',
            label: 'New loan closes',
            date: newLoanEndDate,
            monthsFromNow: analysis.newLoanTenure,
            emiFreed: analysis.newEMI,
            loanType: 'New Loan'
        });
    }

    // Sort milestones by date
    milestones.sort((a, b) => a.monthsFromNow - b.monthsFromNow);

    // Calculate cumulative EMI freed at each milestone
    let cumulativeEMIFreed = 0;
    milestones.forEach(m => {
        cumulativeEMIFreed += m.emiFreed;
        m.cumulativeEMIFreed = cumulativeEMIFreed;
        m.remainingEMI = analysis.combinedEMI - cumulativeEMIFreed;
    });

    // Debt-free date
    const debtFreeDate = milestones.length > 0 ? milestones[milestones.length - 1].date : today;
    const debtFreeMonths = analysis.debtFreeMonths;
    const debtFreeYears = Math.floor(debtFreeMonths / 12);
    const debtFreeRemainingMonths = debtFreeMonths % 12;

    // Progress percentage (if any loans are partially paid)
    const totalOriginalTenure = existingLoans.reduce((sum, l) => sum + (parseInt(l.tenure_months || l.tenure || 0)), 0) + analysis.newLoanTenure;
    const totalPaidTenure = existingLoans.reduce((sum, l) => sum + (parseInt(l.paid_tenure || 0)), 0);
    const overallProgress = totalOriginalTenure > 0 ? (totalPaidTenure / totalOriginalTenure) * 100 : 0;

    return {
        milestones,
        debtFreeDate,
        debtFreeMonths,
        debtFreeYears,
        debtFreeRemainingMonths,
        debtFreeDateFormatted: debtFreeDate.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
        overallProgress: parseFloat(overallProgress.toFixed(1)),
        totalMilestones: milestones.length
    };
};

/**
 * Calculate loan repayment priority analysis
 * Determines which loans should be paid off first based on cost efficiency
 * @param {Array} existingLoans - Existing loans array
 * @param {Object} analysis - Analysis metrics
 * @returns {Object} Priority analysis with ranked loans
 */
export const calculateLoanRepaymentPriority = (existingLoans = [], analysis) => {
    // Calculate priority metrics for each loan
    const loansWithPriority = existingLoans.map((loan, index) => {
        const outstanding = parseFloat(loan.outstanding_loan || loan.outstanding_amount || 0);
        const emi = parseFloat(loan.emi || loan.emi_amount || 0);
        const interestRate = parseFloat(loan.interest_rate || 0);
        const remainingTenure = parseInt(loan.remaining_tenure || loan.tenure_remaining || 0);

        // Calculate monthly interest cost (interest portion of payment)
        const monthlyInterestRate = interestRate / 12 / 100;
        const monthlyInterestCost = outstanding * monthlyInterestRate;
        const monthlyPrincipalPaid = emi - monthlyInterestCost;

        // Calculate total remaining interest
        const totalRemainingPayment = emi * remainingTenure;
        const totalRemainingInterest = parseFloat(loan.outstanding_interest) ||
            Math.max(0, totalRemainingPayment - outstanding);

        // Interest cost per ₹1 Lakh of outstanding
        const interestCostPerLakh = outstanding > 0
            ? (totalRemainingInterest / outstanding) * 100000
            : 0;

        // Average monthly interest cost over remaining tenure
        const avgMonthlyInterestCost = remainingTenure > 0
            ? totalRemainingInterest / remainingTenure
            : 0;

        // Interest to EMI ratio (what portion of EMI goes to interest)
        const interestToEMIRatio = emi > 0 ? (monthlyInterestCost / emi) * 100 : 0;

        // Cost efficiency score (higher = more costly = higher priority to pay off)
        // Weighted: 40% interest rate + 30% interest cost per lakh + 30% interest ratio
        const priorityScore = Math.min(100, Math.round(
            (interestRate / 24 * 100) * 0.4 +  // Normalize to 24% max rate
            (interestCostPerLakh / 50000 * 100) * 0.3 +  // Normalize to 50k
            interestToEMIRatio * 0.3
        ));

        return {
            id: index,
            loanId: loan.id,
            lender: loan.lender_name || loan.lender || 'Bank',
            loanType: loan.loan_type || loan.type || 'Loan',
            outstanding,
            emi,
            interestRate,
            remainingTenure,
            monthlyInterestCost: Math.round(monthlyInterestCost),
            monthlyPrincipalPaid: Math.round(monthlyPrincipalPaid),
            totalRemainingInterest: Math.round(totalRemainingInterest),
            interestCostPerLakh: Math.round(interestCostPerLakh),
            avgMonthlyInterestCost: Math.round(avgMonthlyInterestCost),
            interestToEMIRatio: parseFloat(interestToEMIRatio.toFixed(1)),
            priorityScore
        };
    });

    // Sort by priority score (highest first) to determine rank
    const sortedLoans = [...loansWithPriority].sort((a, b) => b.priorityScore - a.priorityScore);

    // Assign ranks and labels
    sortedLoans.forEach((loan, index) => {
        loan.priorityRank = index + 1;

        if (index === 0) {
            loan.priorityLabel = 'Pay First';
            loan.priorityColor = '#ef4444'; // Red
            loan.prioritySeverity = 'danger';
            loan.recommendation = 'Highest cost - prioritize paying off this loan first';
        } else if (loan.priorityScore >= 70) {
            loan.priorityLabel = 'High Priority';
            loan.priorityColor = '#f97316'; // Orange
            loan.prioritySeverity = 'warning';
            loan.recommendation = 'High interest burden - consider early repayment';
        } else if (loan.priorityScore >= 40) {
            loan.priorityLabel = 'Medium Priority';
            loan.priorityColor = '#eab308'; // Yellow
            loan.prioritySeverity = 'info';
            loan.recommendation = 'Moderate cost - pay after high priority loans';
        } else {
            loan.priorityLabel = 'Low Priority';
            loan.priorityColor = '#22c55e'; // Green
            loan.prioritySeverity = 'success';
            loan.recommendation = 'Lower cost - continue regular payments';
        }
    });

    // Add new loan to the analysis
    const newLoanMonthlyInterest = analysis.newLoanAmount * (analysis.newLoanRate / 12 / 100);
    const newLoanInterestCostPerLakh = analysis.newLoanAmount > 0
        ? (analysis.newLoanTotalInterest / analysis.newLoanAmount) * 100000
        : 0;
    const newLoanInterestRatio = analysis.newEMI > 0
        ? (newLoanMonthlyInterest / analysis.newEMI) * 100
        : 0;
    const newLoanPriorityScore = Math.min(100, Math.round(
        (analysis.newLoanRate / 24 * 100) * 0.4 +
        (newLoanInterestCostPerLakh / 50000 * 100) * 0.3 +
        newLoanInterestRatio * 0.3
    ));

    const newLoan = {
        id: 'new',
        lender: 'New Loan',
        loanType: 'Proposed',
        outstanding: analysis.newLoanAmount,
        emi: analysis.newEMI,
        interestRate: analysis.newLoanRate,
        remainingTenure: analysis.newLoanTenure,
        monthlyInterestCost: Math.round(newLoanMonthlyInterest),
        monthlyPrincipalPaid: Math.round(analysis.newEMI - newLoanMonthlyInterest),
        totalRemainingInterest: Math.round(analysis.newLoanTotalInterest),
        interestCostPerLakh: Math.round(newLoanInterestCostPerLakh),
        avgMonthlyInterestCost: analysis.newLoanTenure > 0
            ? Math.round(analysis.newLoanTotalInterest / analysis.newLoanTenure)
            : 0,
        interestToEMIRatio: parseFloat(newLoanInterestRatio.toFixed(1)),
        priorityScore: newLoanPriorityScore,
        isNewLoan: true
    };

    // Determine new loan's rank among all loans
    const allLoansForRanking = [...sortedLoans, newLoan].sort((a, b) => b.priorityScore - a.priorityScore);
    const newLoanRank = allLoansForRanking.findIndex(l => l.id === 'new') + 1;
    newLoan.priorityRank = newLoanRank;

    if (newLoanRank === 1) {
        newLoan.priorityLabel = 'Highest Cost';
        newLoan.priorityColor = '#ef4444';
        newLoan.prioritySeverity = 'danger';
    } else if (newLoanPriorityScore >= 70) {
        newLoan.priorityLabel = 'High Cost';
        newLoan.priorityColor = '#f97316';
        newLoan.prioritySeverity = 'warning';
    } else if (newLoanPriorityScore >= 40) {
        newLoan.priorityLabel = 'Medium Cost';
        newLoan.priorityColor = '#eab308';
        newLoan.prioritySeverity = 'info';
    } else {
        newLoan.priorityLabel = 'Low Cost';
        newLoan.priorityColor = '#22c55e';
        newLoan.prioritySeverity = 'success';
    }

    // Summary calculations
    const highestCostLoan = sortedLoans[0] || null;
    const totalMonthlyInterest = loansWithPriority.reduce((sum, l) => sum + l.monthlyInterestCost, 0) +
        Math.round(newLoanMonthlyInterest);

    // Calculate potential savings if highest cost loan is paid first
    // Rough estimate: 20% of remaining interest could be saved with accelerated payment
    const potentialSavings = highestCostLoan
        ? Math.round(highestCostLoan.totalRemainingInterest * 0.2)
        : 0;

    // Determine optimal strategy
    const avgInterestRate = loansWithPriority.length > 0
        ? loansWithPriority.reduce((sum, l) => sum + l.interestRate, 0) / loansWithPriority.length
        : 0;

    let optimalStrategy = 'avalanche'; // Default: pay highest interest first
    let strategyReason = 'Pay highest interest rate loans first to minimize total interest paid';

    // If loan amounts vary significantly, snowball might be psychologically better
    const amounts = loansWithPriority.map(l => l.outstanding);
    const maxAmount = Math.max(...amounts, 1);
    const minAmount = Math.min(...amounts, 1);
    if (maxAmount / minAmount > 5 && minAmount < 100000) {
        optimalStrategy = 'snowball';
        strategyReason = 'Small loans can be cleared quickly for psychological wins and freed EMI';
    }

    return {
        loans: sortedLoans,
        newLoan,
        summary: {
            highestCostLoan: highestCostLoan ? `${highestCostLoan.lender} ${highestCostLoan.loanType}` : 'N/A',
            highestCostLoanRate: highestCostLoan?.interestRate || 0,
            totalMonthlyInterest,
            potentialSavings,
            optimalStrategy,
            strategyReason,
            avgInterestRate: parseFloat(avgInterestRate.toFixed(2)),
            totalLoans: loansWithPriority.length + 1 // Including new loan
        }
    };
};

/**
 * Calculate prepayment impact analysis
 * Simulates the effect of extra payments on loans
 * @param {Array} existingLoans - Existing loans array
 * @param {Object} analysis - Analysis metrics
 * @param {number} extraPayment - Extra monthly payment amount
 * @param {string} strategy - Allocation strategy: 'avalanche' | 'snowball' | 'proportional'
 * @param {Object} customAllocation - Custom allocation object { loanId: amount }
 * @returns {Object} Prepayment impact analysis
 */
export const calculatePrepaymentImpact = (
    existingLoans = [],
    analysis,
    extraPayment = 0,
    strategy = 'avalanche',
    customAllocation = null
) => {
    if (extraPayment <= 0) {
        return {
            allocations: [],
            totalImpact: {
                totalMonthsSaved: 0,
                totalInterestSaved: 0,
                originalDebtFreeDate: '',
                newDebtFreeDate: '',
                yearsSaved: 0
            },
            strategy,
            extraPayment: 0
        };
    }

    // Prepare all loans (existing + new)
    const allLoans = existingLoans.map((loan, index) => ({
        id: index,
        loanId: loan.id,
        lender: loan.lender_name || loan.lender || 'Bank',
        loanType: loan.loan_type || loan.type || 'Loan',
        outstanding: parseFloat(loan.outstanding_loan || loan.outstanding_amount || 0),
        emi: parseFloat(loan.emi || loan.emi_amount || 0),
        interestRate: parseFloat(loan.interest_rate || 0),
        remainingTenure: parseInt(loan.remaining_tenure || loan.tenure_remaining || 0),
        isNewLoan: false
    }));

    // Add new loan
    allLoans.push({
        id: 'new',
        lender: 'New Loan',
        loanType: 'Proposed',
        outstanding: analysis.newLoanAmount,
        emi: analysis.newEMI,
        interestRate: analysis.newLoanRate,
        remainingTenure: analysis.newLoanTenure,
        isNewLoan: true
    });

    // Sort loans based on strategy
    let sortedLoans = [...allLoans];
    if (strategy === 'avalanche') {
        // Highest interest rate first
        sortedLoans.sort((a, b) => b.interestRate - a.interestRate);
    } else if (strategy === 'snowball') {
        // Smallest balance first
        sortedLoans.sort((a, b) => a.outstanding - b.outstanding);
    }
    // For 'proportional' or 'custom', we don't need to sort

    // Calculate allocations
    let remainingExtra = extraPayment;
    const allocations = [];

    if (strategy === 'custom' && customAllocation) {
        // Custom allocation
        sortedLoans.forEach(loan => {
            const allocation = customAllocation[loan.id] || 0;
            const actualAllocation = Math.min(allocation, remainingExtra);
            remainingExtra -= actualAllocation;

            const impact = calculateSingleLoanPrepaymentImpact(loan, actualAllocation);
            allocations.push({
                ...loan,
                ...impact,
                extraAllocation: actualAllocation
            });
        });
    } else if (strategy === 'proportional') {
        // Distribute proportionally based on outstanding balance
        const totalOutstanding = sortedLoans.reduce((sum, l) => sum + l.outstanding, 0);
        sortedLoans.forEach(loan => {
            const proportion = totalOutstanding > 0 ? loan.outstanding / totalOutstanding : 0;
            const allocation = Math.round(extraPayment * proportion);

            const impact = calculateSingleLoanPrepaymentImpact(loan, allocation);
            allocations.push({
                ...loan,
                ...impact,
                extraAllocation: allocation
            });
        });
    } else {
        // Avalanche or Snowball: allocate to one loan at a time
        sortedLoans.forEach(loan => {
            let allocation = 0;
            if (remainingExtra > 0) {
                // For simplicity, allocate all extra to the top priority loan
                // In a more sophisticated model, we'd check if the loan can absorb the payment
                allocation = remainingExtra;
                remainingExtra = 0;
            }

            const impact = calculateSingleLoanPrepaymentImpact(loan, allocation);
            allocations.push({
                ...loan,
                ...impact,
                extraAllocation: allocation
            });
        });
    }

    // Calculate total impact
    const totalMonthsSaved = allocations.reduce((sum, a) => sum + (a.monthsSaved || 0), 0);
    const totalInterestSaved = allocations.reduce((sum, a) => sum + (a.interestSaved || 0), 0);

    // Calculate debt-free dates
    const today = new Date();
    const maxOriginalTenure = Math.max(...allLoans.map(l => l.remainingTenure));
    const maxNewTenure = Math.max(...allocations.map(a => a.newTenure || a.remainingTenure));

    const originalDebtFreeDate = new Date(today);
    originalDebtFreeDate.setMonth(originalDebtFreeDate.getMonth() + maxOriginalTenure);

    const newDebtFreeDate = new Date(today);
    newDebtFreeDate.setMonth(newDebtFreeDate.getMonth() + maxNewTenure);

    const yearsSaved = (maxOriginalTenure - maxNewTenure) / 12;

    return {
        allocations: allocations.sort((a, b) => (b.extraAllocation || 0) - (a.extraAllocation || 0)),
        totalImpact: {
            totalMonthsSaved,
            totalInterestSaved: Math.round(totalInterestSaved),
            originalDebtFreeDate: originalDebtFreeDate.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
            newDebtFreeDate: newDebtFreeDate.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
            yearsSaved: parseFloat(yearsSaved.toFixed(1)),
            monthsSavedTotal: maxOriginalTenure - maxNewTenure
        },
        strategy,
        extraPayment
    };
};

/**
 * Helper: Calculate prepayment impact for a single loan
 */
const calculateSingleLoanPrepaymentImpact = (loan, extraPayment) => {
    if (extraPayment <= 0 || loan.outstanding <= 0 || loan.emi <= 0) {
        return {
            newMonthlyPayment: loan.emi,
            originalTenure: loan.remainingTenure,
            newTenure: loan.remainingTenure,
            monthsSaved: 0,
            interestSaved: 0,
            newEndDate: ''
        };
    }

    const monthlyRate = loan.interestRate / 12 / 100;
    const newMonthlyPayment = loan.emi + extraPayment;

    // Calculate new tenure with increased payment
    // Using the formula: n = -log(1 - (P * r / EMI)) / log(1 + r)
    // Where P = principal, r = monthly rate, EMI = new monthly payment
    let newTenure = loan.remainingTenure;

    if (monthlyRate > 0) {
        const numerator = Math.log(newMonthlyPayment / (newMonthlyPayment - loan.outstanding * monthlyRate));
        const denominator = Math.log(1 + monthlyRate);
        newTenure = Math.ceil(numerator / denominator);

        // Ensure new tenure is reasonable
        newTenure = Math.max(1, Math.min(newTenure, loan.remainingTenure));
    } else {
        // No interest case
        newTenure = Math.ceil(loan.outstanding / newMonthlyPayment);
    }

    const monthsSaved = loan.remainingTenure - newTenure;

    // Calculate interest saved
    const originalTotalPayment = loan.emi * loan.remainingTenure;
    const newTotalPayment = newMonthlyPayment * newTenure;
    const originalInterest = originalTotalPayment - loan.outstanding;
    const newInterest = newTotalPayment - loan.outstanding;
    const interestSaved = Math.max(0, originalInterest - newInterest);

    // Calculate new end date
    const today = new Date();
    const newEndDate = new Date(today);
    newEndDate.setMonth(newEndDate.getMonth() + newTenure);

    return {
        newMonthlyPayment,
        originalTenure: loan.remainingTenure,
        newTenure,
        monthsSaved,
        interestSaved: Math.round(interestSaved),
        newEndDate: newEndDate.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
    };
};

/**
 * Calculate what-if scenarios
 * @param {Object} analysis - Analysis metrics
 * @param {Object} selectedOffer - Selected offer
 * @returns {Object} What-if scenario calculations
 */
export const calculateWhatIfScenarios = (analysis, selectedOffer = {}) => {
    const baseEMI = analysis.newEMI;
    const baseTenure = analysis.newLoanTenure;
    const baseLoanAmount = analysis.newLoanAmount;
    const baseRate = analysis.newLoanRate;

    const scenarios = [];

    // Scenario 1: 10% lower loan amount
    const lowerAmount = baseLoanAmount * 0.9;
    const lowerAmountEMI = calculateEMI(lowerAmount, baseRate, baseTenure);
    scenarios.push({
        id: 'lower-amount',
        title: '10% Lower Loan',
        description: 'Reduce loan by 10%',
        change: '-10% Principal',
        loanAmount: lowerAmount,
        emi: lowerAmountEMI,
        tenure: baseTenure,
        emiSaving: baseEMI - lowerAmountEMI,
        totalSaving: (baseEMI - lowerAmountEMI) * baseTenure,
        newFOIR: analysis.netSalary > 0 ? ((analysis.currentTotalEMI + lowerAmountEMI) / analysis.netSalary) * 100 : 0
    });

    // Scenario 2: 12 months longer tenure
    const longerTenure = baseTenure + 12;
    const longerTenureEMI = calculateEMI(baseLoanAmount, baseRate, longerTenure);
    scenarios.push({
        id: 'longer-tenure',
        title: '+1 Year Tenure',
        description: 'Extend tenure by 1 year',
        change: '+12 Months',
        loanAmount: baseLoanAmount,
        emi: longerTenureEMI,
        tenure: longerTenure,
        emiSaving: baseEMI - longerTenureEMI,
        additionalInterest: (longerTenureEMI * longerTenure) - (baseEMI * baseTenure),
        newFOIR: analysis.netSalary > 0 ? ((analysis.currentTotalEMI + longerTenureEMI) / analysis.netSalary) * 100 : 0
    });

    // Scenario 3: 12 months shorter tenure
    if (baseTenure > 12) {
        const shorterTenure = baseTenure - 12;
        const shorterTenureEMI = calculateEMI(baseLoanAmount, baseRate, shorterTenure);
        scenarios.push({
            id: 'shorter-tenure',
            title: '-1 Year Tenure',
            description: 'Reduce tenure by 1 year',
            change: '-12 Months',
            loanAmount: baseLoanAmount,
            emi: shorterTenureEMI,
            tenure: shorterTenure,
            emiIncrease: shorterTenureEMI - baseEMI,
            interestSaving: (baseEMI * baseTenure) - (shorterTenureEMI * shorterTenure),
            newFOIR: analysis.netSalary > 0 ? ((analysis.currentTotalEMI + shorterTenureEMI) / analysis.netSalary) * 100 : 0
        });
    }

    // Scenario 4: Extra EMI payment (1 extra EMI per year)
    const effectiveTenure = Math.ceil(baseTenure * 12 / 13); // Rough approximation
    const interestSavedExtra = (baseEMI * baseTenure) - (baseEMI * effectiveTenure);
    scenarios.push({
        id: 'extra-emi',
        title: 'Extra EMI/Year',
        description: 'Pay 1 extra EMI annually',
        change: '+1 EMI/Year',
        loanAmount: baseLoanAmount,
        emi: baseEMI,
        tenure: effectiveTenure,
        monthsSaved: baseTenure - effectiveTenure,
        interestSaving: interestSavedExtra,
        newFOIR: analysis.foirAfter
    });

    return {
        currentScenario: {
            loanAmount: baseLoanAmount,
            emi: baseEMI,
            tenure: baseTenure,
            rate: baseRate,
            totalInterest: analysis.newLoanTotalInterest,
            foir: analysis.foirAfter
        },
        scenarios
    };
};

/**
 * Helper: Calculate EMI
 * @param {number} principal - Loan amount
 * @param {number} annualRate - Annual interest rate (%)
 * @param {number} tenureMonths - Tenure in months
 * @returns {number} EMI amount
 */
const calculateEMI = (principal, annualRate, tenureMonths) => {
    if (principal <= 0 || tenureMonths <= 0) return 0;
    if (annualRate <= 0) return principal / tenureMonths;

    const monthlyRate = annualRate / 12 / 100;
    const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths) /
                (Math.pow(1 + monthlyRate, tenureMonths) - 1);
    return Math.round(emi);
};

/**
 * Calculate Loan Timeline Calendar data
 * Creates a month-year grid showing loan start/end events for all loans
 * @param {Array} existingLoans - Existing loans array
 * @param {Object} analysis - Analysis metrics
 * @param {Object} prepaymentImpact - Optional prepayment impact data (to show prepayment end dates)
 * @returns {Object} Calendar grid data
 */
export const calculateLoanTimelineCalendar = (existingLoans = [], analysis, prepaymentImpact = null) => {
    const today = new Date();
    const currentMonth = today.getMonth(); // 0-indexed
    const currentYear = today.getFullYear();

    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Build loan entries with start and end dates
    const loans = [];

    existingLoans.forEach((loan, index) => {
        const remainingTenure = parseInt(loan.remaining_tenure || loan.tenure_remaining || 0);
        const lender = loan.lender_name || loan.lender || 'Loan';
        const loanType = loan.loan_type || loan.type || 'Loan';
        const shortLabel = `${lender.substring(0, 10)}${lender.length > 10 ? '..' : ''}`;
        const emi = parseFloat(loan.emi || loan.emi_amount || 0);
        const loanAmount = parseFloat(loan.original_loan_amount || loan.outstanding_loan || loan.outstanding_amount || 0);
        const interestRate = parseFloat(loan.interest_rate || 0);

        // Use actual loan_start_date if available, otherwise calculate from remaining tenure
        let startDate;
        if (loan.loan_start_date) {
            startDate = new Date(loan.loan_start_date);
        } else {
            const totalTenure = parseInt(loan.tenure_months || loan.tenure || 0);
            startDate = new Date(today);
            if (totalTenure > 0 && remainingTenure > 0) {
                startDate.setMonth(startDate.getMonth() - (totalTenure - remainingTenure));
            }
        }

        // Use actual loan_end_date if available, otherwise calculate
        let endDate;
        if (loan.loan_end_date) {
            endDate = new Date(loan.loan_end_date);
        } else {
            endDate = new Date(today);
            endDate.setMonth(endDate.getMonth() + remainingTenure);
        }

        // Calculate prepayment end date if available
        let prepayEndDate = null;
        if (prepaymentImpact?.allocations) {
            const allocation = prepaymentImpact.allocations.find(a => a.id === index);
            if (allocation && allocation.monthsSaved > 0) {
                prepayEndDate = new Date(today);
                const newTenure = allocation.newTenure || remainingTenure;
                prepayEndDate.setMonth(prepayEndDate.getMonth() + newTenure);
            }
        }

        loans.push({
            id: `existing-${index}`,
            label: shortLabel,
            lender,
            loanType,
            fullLabel: `${lender} - ${loanType}`,
            emi,
            loanAmount,
            interestRate,
            isNew: false,
            startMonth: startDate.getMonth(),
            startYear: startDate.getFullYear(),
            endMonth: endDate.getMonth(),
            endYear: endDate.getFullYear(),
            prepayEndMonth: prepayEndDate ? prepayEndDate.getMonth() : null,
            prepayEndYear: prepayEndDate ? prepayEndDate.getFullYear() : null,
            color: `hsl(${(index * 50 + 210) % 360}, 60%, 55%)`
        });
    });

    // Add new loan
    if (analysis?.newLoanTenure > 0) {
        const newStartDate = new Date(today);
        const newEndDate = new Date(today);
        newEndDate.setMonth(newEndDate.getMonth() + analysis.newLoanTenure);

        let prepayEndDate = null;
        if (prepaymentImpact?.allocations) {
            const allocation = prepaymentImpact.allocations.find(a => a.id === 'new');
            if (allocation && allocation.monthsSaved > 0) {
                prepayEndDate = new Date(today);
                prepayEndDate.setMonth(prepayEndDate.getMonth() + (allocation.newTenure || analysis.newLoanTenure));
            }
        }

        loans.push({
            id: 'new-loan',
            label: 'New Loan',
            lender: 'New Loan',
            loanType: analysis.loanProduct || 'Proposed',
            fullLabel: `New Loan - ${analysis.loanProduct || 'Proposed'}`,
            emi: analysis.newEMI || 0,
            loanAmount: analysis.newLoanAmount || 0,
            interestRate: analysis.newLoanRate || 0,
            isNew: true,
            startMonth: newStartDate.getMonth(),
            startYear: newStartDate.getFullYear(),
            endMonth: newEndDate.getMonth(),
            endYear: newEndDate.getFullYear(),
            prepayEndMonth: prepayEndDate ? prepayEndDate.getMonth() : null,
            prepayEndYear: prepayEndDate ? prepayEndDate.getFullYear() : null,
            color: '#3B82F6'
        });
    }

    // Determine year range for the calendar
    if (loans.length === 0) {
        return { loans: [], years: [], months: MONTH_NAMES, events: [], currentMonth, currentYear };
    }

    // Collect all years that have events (start, end, prepay-end)
    const eventYears = new Set();
    // Always include current year
    eventYears.add(currentYear);
    loans.forEach(loan => {
        eventYears.add(loan.startYear);
        eventYears.add(loan.endYear);
        if (loan.prepayEndYear) eventYears.add(loan.prepayEndYear);
    });

    // Only show years that have events, plus one year before and after each event year
    // to give context, but skip large gaps of empty years
    const eventYearsArray = Array.from(eventYears).sort((a, b) => a - b);
    const yearsToShow = new Set();

    eventYearsArray.forEach(year => {
        yearsToShow.add(year);
        // Add neighboring years for context (1 year before & after)
        yearsToShow.add(year - 1);
        yearsToShow.add(year + 1);
    });

    // Build final years array, sorted, and mark gaps
    const minYear = Math.min(...eventYearsArray);
    const maxYear = Math.max(...eventYearsArray);
    const years = [];
    let lastAddedYear = null;

    for (let y = minYear; y <= maxYear; y++) {
        if (yearsToShow.has(y)) {
            // If there's a gap, add a separator marker
            if (lastAddedYear !== null && y - lastAddedYear > 1) {
                years.push({ year: null, isGap: true, gapFrom: lastAddedYear + 1, gapTo: y - 1 });
            }
            years.push({ year: y, isGap: false });
            lastAddedYear = y;
        }
    }

    // Build events map: key = "month-year", value = array of events
    const events = {};
    const eventDetails = {
        lender: '',
        loanType: '',
        emi: 0,
        loanAmount: 0,
        interestRate: 0
    };

    loans.forEach(loan => {
        const details = {
            lender: loan.lender,
            loanType: loan.loanType,
            emi: loan.emi,
            loanAmount: loan.loanAmount,
            interestRate: loan.interestRate
        };

        // Start event
        const startKey = `${loan.startMonth}-${loan.startYear}`;
        if (!events[startKey]) events[startKey] = [];
        events[startKey].push({
            loanId: loan.id,
            type: 'start',
            label: loan.label,
            fullLabel: loan.fullLabel,
            isNew: loan.isNew,
            color: loan.color,
            ...details
        });

        // End event (original)
        const endKey = `${loan.endMonth}-${loan.endYear}`;
        if (!events[endKey]) events[endKey] = [];
        events[endKey].push({
            loanId: loan.id,
            type: 'end',
            label: loan.label,
            fullLabel: loan.fullLabel,
            isNew: loan.isNew,
            color: loan.color,
            ...details
        });

        // Prepayment end event (if different from original end)
        if (loan.prepayEndMonth !== null && loan.prepayEndYear !== null) {
            const prepayKey = `${loan.prepayEndMonth}-${loan.prepayEndYear}`;
            if (prepayKey !== endKey) {
                if (!events[prepayKey]) events[prepayKey] = [];
                events[prepayKey].push({
                    loanId: loan.id,
                    type: 'prepay-end',
                    label: loan.label,
                    fullLabel: loan.fullLabel,
                    isNew: loan.isNew,
                    color: loan.color,
                    ...details
                });
            }
        }
    });

    return {
        loans,
        years,
        months: MONTH_NAMES,
        events,
        currentMonth,
        currentYear
    };
};

export default {
    calculateAnalysis,
    calculateAffordabilityScore,
    generateRecommendations,
    formatCurrency,
    formatPercentage,
    preparePortfolioData,
    calculateSalaryImpact,
    calculateEligibilityUtilization,
    prepareLoanComparisonMatrix,
    calculateCashFlowImpact,
    calculateRiskAssessment,
    calculateInterestCostAnalysis,
    calculateDebtFreedomTimeline,
    calculateWhatIfScenarios,
    calculateLoanRepaymentPriority,
    calculatePrepaymentImpact,
    calculateLoanTimelineCalendar
};
